/**
 * @file Auth-verify fallback route (`/auth/verify`) — should rarely
 *       render, since lab's `GET /api/auth/verify` 302s the learner
 *       past the SPA root with the cookie set.
 * @module @labcraft/lab/ui/pages/auth-verify
 *
 * If the learner lands here (e.g. the lab redirect failed for some
 * reason and the SPA matched the URL anyway), we invalidate `qkMe`
 * so the freshly-minted session is picked up, then re-derive the
 * redirect from the resolved learner's `(status, profileCompletedAt)`
 * — same rules the backend's `resolvePostAuthRedirect` enforces on
 * the happy path.
 */

import { useEffect, type JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { resolveLandingForLearner } from '../../lib/landing.ts'
import { useMe } from '../../hooks/use_me.ts'
import { qkMe } from '../../hooks/query_keys.ts'
import styles from './auth.module.css'

export function AuthVerifyPage(): JSX.Element {
  const queryClient = useQueryClient()
  const me = useMe()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const next = search.get('next') ?? '/'

  // Invalidate qkMe exactly once on mount — the cookie was just set
  // by the verify-redirect hop, and the cache may still hold the
  // pre-signin anonymous answer.
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: qkMe })
    // Intentionally fire-once; subsequent renders are driven by the
    // useMe() effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (me.isPending) return
    const learner = me.data?.learner ?? null
    if (!learner) {
      navigate('/auth/expired', { replace: true })
      return
    }
    navigate(resolveLandingForLearner(learner, next), { replace: true })
  }, [me.isPending, me.data, navigate, next])

  return (
    <main className={styles.main}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>Verifying</p>
        <h1 className={styles.title}>Signing you in…</h1>
        <p className={styles.lede}>Hang tight — this only takes a second.</p>
      </section>
    </main>
  )
}
